import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axiosInstance";
import moment from "moment";
import { useAuth } from "../../AuthContext";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ReceivedInvitations = () => {
  const { t, i18n } = useTranslation();
  const languageDirection = i18n.language === "ar" ? "rtl" : "ltr";
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const { userId } = useAuth();
  const [loading, setLoading] = useState(true);

  const fetchInvitations = async () => {
    try {
      const response = await axiosInstance.get(
        "/api/v1/speakers/speakerprofile/invitations-received"
      );
      setInvitations(response.data.items);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.title ||
        (error?.response?.data?.errors?.organizerRatingBySpeaker &&
          error.response.data.errors.organizerRatingBySpeaker.join(", ")) ||
        error?.message ||
        t("messages.errorMessages.unexpectedError");

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (
    invitationId,
    eventId,
    responseMessage
  ) => {
    try {
      await axiosInstance.post(
        `/api/v1/speakers/events/${eventId}/invitations/${invitationId}/response`,
        {
          responseMessage:
            responseMessage ||
            t("messages.successMessages.invitationAcceptSuccess"),
          status: 3,
        }
      );
      toast.success(t("messages.successMessages.invitationAcceptSuccess"));
      fetchInvitations();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.detail || // رسالة الخطأ المفصلة
        error?.response?.data?.message || // أو رسالة بديلة
        error?.message || // رسالة الخطأ العامة
        t("messages.errorMessages.unexpectedError"); // رسالة افتراضية

      toast.error(errorMessage);
    }
  };

  const handleRejectInvitation = async (
    invitationId,
    eventId,
    responseMessage
  ) => {
    try {
      await axiosInstance.post(
        `/api/v1/speakers/events/${eventId}/invitations/${invitationId}/response`,
        {
          responseMessage:
            responseMessage ||
            t("messages.successMessages.invitationRejectSuccess"),
          status: 4,
        }
      );
      toast.success(t("messages.successMessages.invitationRejectSuccess"));
      fetchInvitations();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.title ||
        (error?.response?.data?.errors?.organizerRatingBySpeaker &&
          error.response.data.errors.organizerRatingBySpeaker.join(", ")) ||
        error?.message ||
        t("messages.errorMessages.unexpectedError");

      toast.error(errorMessage);
    }
  };

  const handleRateInvitation = async (eventId, invitationId) => {
    const rating = prompt(t("Please enter your rating (1-5):"));
    const review = prompt(t("Please enter your review:"));

    if (rating && review) {
      try {
        await axiosInstance.post(
          `/api/v1/speakers/events/${eventId}/invitations/${invitationId}/organizer-rate`,
          {
            organizerRatingBySpeaker: rating,
            organizerReviewBySpeaker: review,
          }
        );
        toast.success(t("Invitation rated successfully!"));
        fetchInvitations();
      } catch (error) {
        const errorData = error?.response?.data;

        const detailedError =
          errorData?.errors?.organizerRatingBySpeaker?.join(", ") || // ⬅️ هنا أهم سطر
          errorData?.detail ||
          errorData?.title ||
          error.message ||
          t("messages.errorMessages.unexpectedError");

        toast.error(detailedError);
      }
    } else {
      toast.error(t("Please enter your rating and review."));
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [t]);

  return (
    <div dir={languageDirection}>
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card shadow-sm border-0 rounded-3 mb-5">
                <div className="card-body p-5">
                  {loading ? (
                    <div className="text-center py-5 fs-5 text-secondary">
                      {t("Loading...")}
                    </div>
                  ) : invitations.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="fas fa-envelope-open-text fa-5x text-primary mb-4"></i>
                      <h3 className="fw-bold mb-3">
                        {t("No invitations currently")}
                      </h3>
                      <p className="text-muted fs-6 mb-4">
                        {t("Browse events and request to join here")}
                      </p>
                      <Link
                        to="/events"
                        className="btn btn-primary btn-lg px-4"
                      >
                        {t("Browse Events")}
                      </Link>
                    </div>
                  ) : (
                    invitations.map((invitation) => (
                      <div
                        key={invitation.id}
                        className={`mb-4 p-4 bg-white border-start border-5 rounded-3 shadow-sm position-relative ${
                          invitation.status === 1
                            ? "border-warning"
                            : invitation.status === 3 &&
                              invitation.isRatedBySpeaker
                            ? "border-success"
                            : invitation.status === 4
                            ? "border-danger"
                            : "border-secondary"
                        }`}
                      >
                        <h5 className="fw-semibold">{invitation.eventTitle}</h5>
                        <p className="text-muted mb-1">
                          <i className="far fa-calendar-alt me-1"></i>
                          {moment(invitation.eventDate).format("LL")}
                        </p>
                        <p className="text-secondary mb-3">
                          <i className="fas fa-user me-1"></i>
                          {invitation.organizerName}
                        </p>

                        <ul className="list-unstyled small text-muted mb-3">
                          <li className="mb-1">
                            <strong>{t("Invitation Message")}:</strong>{" "}
                            {invitation.invitationMessage}
                          </li>
                          <li className="mb-1">
                            <strong>{t("Response Message")}:</strong>{" "}
                            {invitation.responseMessage}
                          </li>
                          <li className="mb-1">
                            <strong>{t("Created On")}:</strong>{" "}
                            {moment(invitation.createdOn).format("LLL")}
                          </li>
                          <li className="mb-1">
                            <strong>{t("Updated On")}:</strong>{" "}
                            {moment(invitation.updatedOn).format("LLL")}
                          </li>
                          <li className="mb-1">
                            <strong>{t("Status Name")}:</strong>{" "}
                            {invitation.statusName}
                          </li>
                          <li className="mb-1">
                            <strong>{t("Rated?")}:</strong>{" "}
                            {invitation.isRatedBySpeaker ? t("Yes") : t("No")}
                          </li>
                          <li className="mb-1">
                            <strong>{t("Organizer Rating")}:</strong>{" "}
                            {invitation.organizerRatingBySpeaker ??
                              t("Not rated")}
                          </li>
                          <li className="mb-1">
                            <strong>{t("Organizer Review")}:</strong>{" "}
                            {invitation.organizerReviewBySpeaker ??
                              t("No review")}
                          </li>
                        </ul>

                        {/* أزرار الحالة */}
                        <div className="row g-2 align-items-center">
                          {invitation.status === 1 && (
                            <>
                              <div className="col-auto">
                                <button
                                  className="btn btn-success d-flex align-items-center gap-2"
                                  onClick={() =>
                                    handleAcceptInvitation(
                                      invitation.id,
                                      invitation.eventId,
                                      t("Invitation accepted.")
                                    )
                                  }
                                >
                                  <i className="feather-check"></i>{" "}
                                  {t("Accept")}
                                </button>
                              </div>
                              <div className="col-auto">
                                <button
                                  className="btn btn-outline-danger d-flex align-items-center gap-2"
                                  onClick={() =>
                                    handleRejectInvitation(
                                      invitation.id,
                                      invitation.eventId,
                                      t("Invitation rejected.")
                                    )
                                  }
                                >
                                  <i className="feather-x"></i> {t("Reject")}
                                </button>
                              </div>
                            </>
                          )}

                          {invitation.status === 3 &&
                            !invitation.isRatedBySpeaker && (
                              <div className="col-auto">
                                <button
                                  className="btn btn-warning text-white d-flex align-items-center gap-2"
                                  onClick={() =>
                                    handleRateInvitation(
                                      invitation.eventId,
                                      invitation.id
                                    )
                                  }
                                >
                                  <i className="fas fa-star"></i> {t("Rate")}
                                </button>
                              </div>
                            )}

                          {invitation.status === 3 &&
                            invitation.isRatedBySpeaker && (
                              <div className="col-auto">
                                <span className="badge bg-success px-3 py-2 fs-6">
                                  <i className="fas fa-check-circle me-1"></i>{" "}
                                  {t("Rated")}
                                </span>
                              </div>
                            )}

                          {invitation.status === 4 && (
                            <div className="col-auto">
                              <span className="badge bg-danger px-3 py-2 fs-6">
                                <i className="fas fa-ban me-1"></i>{" "}
                                {t("Rejected")}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* وصف الحالة النصي */}
                        <div className="mt-3">
                          {invitation.status === 1 && (
                            <p className="text-muted fst-italic small">
                              {t("Waiting for speaker's response")}
                            </p>
                          )}
                          {invitation.status === 3 &&
                            !invitation.isRatedBySpeaker && (
                              <p className="text-muted fst-italic small">
                                {t("Accepted, waiting for your rating")}
                              </p>
                            )}
                          {invitation.status === 3 &&
                            invitation.isRatedBySpeaker && (
                              <p className="text-muted fst-italic small">
                                {t("Accepted and rated")}
                              </p>
                            )}
                          {invitation.status === 4 && (
                            <p className="text-muted fst-italic small">
                              {t("Invitation was rejected")}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <ToastContainer />
      </section>
    </div>
  );
};

export default ReceivedInvitations;
